import { useState } from 'react';
import { Navigate, useNavigate, Link } from 'react-router-dom';
import { useAuth, ApiError } from '../context/AuthContext';
import { api, configureApi } from '../lib/api';
import { Brandmark } from '../components/Brandmark';

// Dérive un identifiant société valide (regex backend : ^[a-z0-9][a-z0-9-]{1,30}$)
// à partir du nom de la société, pour proposer une valeur par défaut que
// l'utilisateur peut toujours modifier lui-même.
function slugify(v: string): string {
  return v
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 31);
}

export function Signup() {
  const { state, ready, login } = useAuth();
  const navigate = useNavigate();

  const [apiUrl, setApiUrl] = useState('https://api.caissepilot.ma');
  const [inviteCode, setInviteCode] = useState('');
  const [company, setCompany] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [tenantEdited, setTenantEdited] = useState(false);
  const [adminName, setAdminName] = useState('');
  const [adminUser, setAdminUser] = useState('');
  const [pin, setPin] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  if (ready && state.token) return <Navigate to={`/${state.tenant}`} replace />;

  function onCompanyChange(v: string) {
    setCompany(v);
    if (!tenantEdited) setTenantId(slugify(v));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!apiUrl || !inviteCode || !company || !tenantId || !adminUser || !pin) {
      setError('Tous les champs obligatoires doivent être renseignés.');
      return;
    }
    if (pin !== pinConfirm) {
      setError('Les deux codes PIN ne correspondent pas.');
      return;
    }
    setBusy(true);
    try {
      const cleanUrl = apiUrl.trim().replace(/\/+$/, '');
      configureApi(cleanUrl, '');
      await api('/v1/signup', {
        method: 'POST',
        body: {
          invite_code: inviteCode,
          tenant_id: tenantId,
          company,
          admin_user: adminUser,
          admin_name: adminName,
          pin,
        },
      });
      // Le compte et la société viennent d'être créés : on se connecte
      // directement avec les identifiants qui viennent d'être saisis.
      await login(cleanUrl, tenantId, adminUser, pin);
      navigate(`/${tenantId}`, { replace: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Inscription impossible.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-view">
      <section className="stage">
        <div className="stage-brand">
          <span className="mark"></span><span className="name">CaissePilot</span>
          <span className="sep"></span><span>Servicepro Solutions</span>
        </div>
        <div className="stage-pitch">
          <h1>Votre société.<br/>Votre espace.<br/><em>En quelques minutes.</em></h1>
          <p>Créez l'espace CaissePilot de votre société et son premier compte administrateur en une seule
             étape — vous pourrez ensuite inviter le reste de votre équipe.</p>
          <div className="stage-features">
            <div className="stage-feature"><span className="dot"></span>Un espace isolé par société</div>
            <div className="stage-feature"><span className="dot"></span>Le compte administrateur est créé automatiquement</div>
            <div className="stage-feature"><span className="dot"></span>Accès protégé par un code d'invitation</div>
          </div>
        </div>
        <div className="stage-foot">Inscription réservée aux personnes disposant d'un code d'invitation valide.</div>
      </section>
      <section className="formside">
        <div className="inner">
          <Brandmark subtitle="Servicepro Solutions · Emballage" />
          <div className="card">
            <h2>Créer votre société</h2>
            <form onSubmit={onSubmit}>
              <label htmlFor="apiUrl">Adresse de l'API</label>
              <input id="apiUrl" value={apiUrl} onChange={(e) => setApiUrl(e.target.value)} placeholder="https://api.exemple.com" />

              <label htmlFor="inviteCode">Code d'invitation</label>
              <input id="inviteCode" value={inviteCode} onChange={(e) => setInviteCode(e.target.value)} placeholder="Fourni par votre administrateur" />

              <label htmlFor="company">Nom de la société</label>
              <input id="company" value={company} onChange={(e) => onCompanyChange(e.target.value)} placeholder="ex. Servicepro Solutions" />

              <label htmlFor="tenantId">Identifiant société (dans l'URL de l'appli)</label>
              <input
                id="tenantId"
                value={tenantId}
                onChange={(e) => { setTenantId(slugify(e.target.value)); setTenantEdited(true); }}
                placeholder="ex. servicepro"
                autoCapitalize="off"
                autoCorrect="off"
              />
              {tenantId && <div className="sub2">app.caissepilot.ma/{tenantId}/…</div>}

              <label htmlFor="adminName">Votre nom</label>
              <input id="adminName" value={adminName} onChange={(e) => setAdminName(e.target.value)} placeholder="ex. Youssef Alaoui" />

              <label htmlFor="adminUser">Identifiant de connexion</label>
              <input id="adminUser" value={adminUser} onChange={(e) => setAdminUser(e.target.value)} placeholder="ex. admin" autoCapitalize="off" autoCorrect="off" />

              <label htmlFor="pin">Code PIN (4 à 8 chiffres)</label>
              <input id="pin" type="password" inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="4 à 8 chiffres" />

              <label htmlFor="pinConfirm">Confirmer le code PIN</label>
              <input id="pinConfirm" type="password" inputMode="numeric" value={pinConfirm} onChange={(e) => setPinConfirm(e.target.value)} placeholder="Retapez le code PIN" />

              <div className="row" style={{ marginTop: '1rem' }}>
                <button type="submit" className="primary" style={{ width: '100%' }} disabled={busy}>
                  {busy ? 'Création en cours...' : 'Créer la société'}
                </button>
              </div>
              {error && <div className="err">{error}</div>}
            </form>
            <div className="sub2" style={{ marginTop: '1rem' }}>
              Déjà une société ? <Link to="/">Se connecter</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
